import { useMemo } from 'react';
import type { AeroCandidateReviewRow } from '@/features/tradeLogicAuthoring/components/aeroCandidateResultDetails';
import { type AeroObsPalette, buildAeroObsKey } from '../aeroObsPalette';
import { formatAeroCompactNumber } from '../aeroPageUtils';

interface AeroObsFilterProps {
    palette: AeroObsPalette;
    results: AeroCandidateReviewRow[];
    activeObsKey: string | null;
    onChange: (obsKey: string | null) => void;
}

const buildObsCountsByKey = (results: AeroCandidateReviewRow[]): Map<string, number> => {
    const counts = new Map<string, number>();

    for (const candidate of results) {
        const key = buildAeroObsKey(candidate.optionBuyingStrategyId);
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    return counts;
};

const AeroObsFilter = ({ palette, results, activeObsKey, onChange }: AeroObsFilterProps) => {
    const counts = useMemo(() => buildObsCountsByKey(results), [results]);

    if (!palette.hasMultipleAxes) return null;

    const totalCount = results.length;

    const renderPill = ({
        active,
        label,
        count,
        toneClassName,
        onClick
    }: {
        active: boolean;
        label: string;
        count: number;
        toneClassName?: string;
        onClick: () => void;
    }) => {
        const baseClassName = active
            ? (toneClassName ?? 'bg-cyan-600 text-white border-cyan-500')
            : 'bg-black/30 text-zinc-300 border-zinc-700 hover:bg-black/50';

        return (
            <button
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${baseClassName}`}
                type="button"
                onClick={onClick}
            >
                <span className="truncate">{label}</span>
                <span className={`rounded-full px-1.5 text-[10px] ${active ? 'bg-black/30' : 'bg-zinc-800 text-zinc-400'}`}>
                    {formatAeroCompactNumber(count)}
                </span>
            </button>
        );
    };

    return (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
            <div className="mr-1 text-[11px] uppercase tracking-wide text-zinc-500">Axis</div>
            {renderPill({
                active: activeObsKey === null,
                label: 'All',
                count: totalCount,
                onClick: () => onChange(null)
            })}
            {palette.tones.map((tone) => {
                const count = counts.get(tone.key) ?? 0;
                if (count === 0) return null;

                return (
                    <div className="flex items-center gap-1.5" key={tone.key}>
                        <span aria-hidden="true" className={`h-2 w-2 rounded-full ${tone.dotClassName}`} />
                        {renderPill({
                            active: activeObsKey === tone.key,
                            label: tone.shortLabel,
                            count,
                            toneClassName: `${tone.solidChipClassName} border-transparent`,
                            onClick: () => onChange(activeObsKey === tone.key ? null : tone.key)
                        })}
                    </div>
                );
            })}
        </div>
    );
};

export default AeroObsFilter;
